import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, Flex, Icon } from '@kognitos/lattice';

export interface IRainMaxIterationsCardProps {
  /**
   * Number of tool-loop turns the build hit before the backend parked it (the
   * `(N)` from the "maximum iterations" message). Absent when the message
   * didn't carry a parseable count — the copy then omits the number.
   */
  turnCount?: number;
  /**
   * True once the user has picked an option. The card freezes read-only in
   * place (the chosen reply is appended as a separate outgoing message), so it
   * mirrors the answered-interrupt rendering rather than disappearing.
   */
  answered?: boolean;
  /** Which option the user picked, hydrated on history replay of an answered card. */
  choseContinue?: boolean;
  isSubmitting?: boolean;
  onChoose?: (shouldContinue: boolean) => void;
  className?: string;
}

/**
 * The tool-loop iteration cap is surfaced as a question card — the
 * same visual pattern as `RainInterruptCard` — instead of a raw error toast.
 * The build stalled because it hit the backend's per-batch turn limit, which is
 * usually a sign the problem is too big for one pass. The two options let the
 * user re-arm the loop for another batch or take over and break the work down.
 */
export function RainMaxIterationsCard({
  turnCount,
  answered = false,
  choseContinue,
  isSubmitting = false,
  onChoose,
  className,
}: IRainMaxIterationsCardProps) {
  const { t } = useTranslation('rain');

  const handleChoose = useCallback(
    (shouldContinue: boolean) => {
      if (answered || isSubmitting || !onChoose) return;
      onChoose(shouldContinue);
    },
    [answered, isSubmitting, onChoose],
  );

  const question =
    turnCount !== undefined
      ? t('rain.maxIterations.question', { count: turnCount })
      : t('rain.maxIterations.questionNoCount');

  const options = [
    { label: t('rain.maxIterations.continue'), value: true },
    { label: t('rain.maxIterations.breakDown'), value: false },
  ];

  const interactionDisabled = answered || isSubmitting;

  return (
    <Flex
      direction="column"
      gap={4}
      className={cn(
        'relative border border-border bg-secondary rounded-xl pt-6 pb-4 w-full',
        className,
      )}
      data-testid="rain-max-iterations-card"
      data-answered={answered ? 'true' : 'false'}
    >
      <Flex direction="row" align="center" gap={3} className="px-4 w-full">
        <Icon
          type="CircleHelp"
          size="md"
          className="text-card-foreground shrink-0"
        />
        <span className="min-w-0 flex-1 break-words text-lg font-medium leading-7 text-card-foreground">
          {question}
        </span>
      </Flex>

      <Flex
        direction="column"
        gap={1}
        role="radiogroup"
        aria-label={question}
        className="px-3 w-full pb-2"
      >
        {options.map((option, i) => {
          const checked = answered && choseContinue === option.value;
          const onActivate = () => handleChoose(option.value);
          return (
            <div
              key={option.label}
              role="radio"
              aria-checked={checked}
              aria-disabled={interactionDisabled}
              tabIndex={interactionDisabled ? -1 : 0}
              className={cn(
                'flex w-full items-center gap-3 p-2 rounded-lg transition-colors',
                interactionDisabled ? 'cursor-default' : 'cursor-pointer',
                checked
                  ? 'bg-background'
                  : !interactionDisabled && 'hover:bg-background/40',
                isSubmitting && 'opacity-60',
              )}
              onClick={onActivate}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onActivate();
                }
              }}
              data-testid="rain-max-iterations-option"
            >
              <span
                aria-hidden
                className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-background px-1 text-xs font-medium text-secondary-foreground"
              >
                {i + 1}
              </span>
              <span className="min-w-0 flex-1 break-words text-sm font-medium text-card-foreground">
                {option.label}
              </span>
            </div>
          );
        })}
      </Flex>
    </Flex>
  );
}
